import * as Actions from '../actions/questionActions';
import Question from '../../entities/question';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {getSessionFirmId, getSessionUserId} from '../../lib/picos/sessionIdentity';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type QuestionAction = {
    type: string;
    question: Question | null; 
    questionId: string | Question; 
    partial: boolean; 
    lastFetch: number; 
    questions: Question[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Question> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Question[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Question> | null, firmId: string, userId: string, updates?: Question[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'question', 'questions', data ? backupRows(data) : null, updates)
		  .then(result => {
			  // OWA-786 (CXR-4-001): the worker resolves a typed result for every
			  // path — a resolution is NOT an acknowledgement. Only ok: true means
			  // the backup write is durable; anything else is surfaced, never
			  // swallowed, so a skipped or failed write cannot masquerade as one.
			  if (!result?.ok) console.warn(`⮂ questionReducer: backup write for 'questions' did not persist (${result?.reason ?? 'unknown'}): ${result?.message ?? ''}`);
		  })
		  .catch(console.error);
}

/**
 * The identity that names this tab's backup store, or null when this tab has
 * not finished authenticating.
 *
 * OWA-785: this must come from sessionStorage (per tab), never from
 * localStorage (shared by every tab on the origin). With two firms open in two
 * tabs, the localStorage value is whichever firm logged in last, so the other
 * tab persisted its entities into that firm's backup store and they restored
 * into the wrong firm's UI. A partial identity names some other store, so we
 * skip the write rather than misfile it.
 *
 * @returns {{firmId: string, userId: string} | null}
 */
function storageIdentity(): {firmId: string; userId: string} | null {
	const firmId = getSessionFirmId();
	const userId = getSessionUserId();

	if (!firmId || !userId) {
		if (logging) console.debug('⮂ questionReducer: no session identity, skipping backup write');
		return null;
	}

	return {firmId, userId};
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Question>} data
 * @param {Question[]} [updates]
 * @returns {ArrayMap<Question>}
 */
function updateStorage(data: ArrayMap<Question>, updates?: Question[]): ArrayMap<Question> {
	if (logging) console.debug('⮂ questionReducer: updateStorage');

	if (updates && updates.length === 0) return data;

	const identity = storageIdentity();
	if (!identity) return data;

	if (updates) callStorageWorker(null, identity.firmId, identity.userId, updates);
	else callStorageWorker(data, identity.firmId, identity.userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Question[]} [updates]
 * @returns {ArrayMap<Question>}
 */
function updateStorageNoReturn(updates?: Question[]): void {
	if (logging) console.debug('⮂ questionReducer: updateStorageNoReturn');

	if (!updates || updates.length === 0) return;

	const identity = storageIdentity();
	if (!identity) return;

	callStorageWorker(null, identity.firmId, identity.userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Question[]} [data]
 * @returns {ArrayMap<Question>}
 */
function createState(data?: Question[] | ArrayMap<Question>): ArrayMap<Question> {
	if (!data) return ArrayMapRegistry.clone<Question>('question', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Question>('question', data.map(e => e));
    else return ArrayMapRegistry.clone<Question>('question', data);
}

// Whether deleted questions keep their rows (schema includeDeletedInResults). OWA-68
const retainDeleted = false;

/**
 * The full backup snapshot of a collection: one entry per ID. A row when there is one (deleted rows of a
 * type that keeps them included); otherwise a deletion record for an ID whose row was removed. OWA-68
 *
 * @param {ArrayMap<Question>} data
 * @returns {Array<Question | QuestionDeletionRecord>}
 */
function backupRows(data: ArrayMap<Question>): Array<Question | QuestionDeletionRecord> {
	const records: QuestionDeletionRecord[] = data.deletedVersionEntries()
		.filter(([id]) => !data.has(id))
		.map(([id, addedOn]) => ({questionId: id, addedOn, status: 'D', deletionRecord: true}));

	return records.length ? [...data.values(), ...records] : data.values();
}

/**
 * A deletion record read back from the offline backup: evidence that a question was deleted at a
 * version, never a live row. The storage worker writes one for a deleted question whose type does
 * not keep deleted rows. OWA-68
 */
type QuestionDeletionRecord = {questionId: string; addedOn: number; status: 'D'; deletionRecord: true};

const isDeletionRecord = (o: unknown): o is QuestionDeletionRecord => (o as QuestionDeletionRecord)?.deletionRecord === true;

/**
 * Apply one incoming version of a question to a collection the caller owns (a clone), under the one
 * acceptance rule every path uses (OWA-68):
 * - older than the stored row: rejected
 * - an active version equal to a kept deleted row: rejected (it yields only to a strictly newer one)
 * - no stored row, and at or below the recorded deletion version: rejected
 * - an accepted deleted version records its deletion version; the row is kept as a deleted row when
 *   this type keeps deleted rows, otherwise it is removed
 * - an accepted active version is stored (a newer one supersedes the deletion version)
 *
 * @param {ArrayMap<Question>} st
 * @param {Question | QuestionDeletionRecord} incoming
 * @returns {boolean} whether the version was accepted; only accepted versions go to the backup
 */
function applyVersion(st: ArrayMap<Question>, incoming: Question | QuestionDeletionRecord): boolean {
	const id = isDeletionRecord(incoming) ? incoming.questionId : incoming.id;
	const current = st.get(id);

	if (current) {
		if (incoming.addedOn < current.addedOn) return false;
		// A kept deleted row yields only to a strictly newer active version, like a deletion version
		if (current.status === 'D' && incoming.status !== 'D' && incoming.addedOn === current.addedOn) return false;
	}
	else {
		const deletedAt = st.deletedVersion(id);
		if (deletedAt !== undefined && incoming.addedOn <= deletedAt) return false;
	}

	if (incoming.status === 'D') {
		st.recordDeletion(id, incoming.addedOn);
		if (retainDeleted && !isDeletionRecord(incoming)) st.set(incoming);
		else if (current) st.delete(current);
	}
	else st.set(incoming as Question);

	return true;
}

/**
 * A new indexed collection holding the rows and deletion versions of the given one. Fetch paths build
 * from this instead of cloning, because the initial store state is a bare ArrayMap without the
 * schema's indexes. OWA-68
 *
 * @param {ArrayMap<Question>} from
 * @returns {ArrayMap<Question>}
 */
function carryOver(from: ArrayMap<Question>): ArrayMap<Question> {
	const next = createState(from);
	from.deletedVersionEntries().forEach(([id, version]) => next.recordDeletion(id, version));
	return next;
}

function questions(state: ArrayMap<Question>, action: QuestionAction): ArrayMap<Question> {
	if (!state) state = createState();

	let st: ArrayMap<Question>;

	switch (action.type) {
		// action.question - the new value of question with id action.id
		// action.questionId - id of the target, used if new value is null
		case Actions.QUESTION_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ questionReducer: FETCH_ONE_SUCCESS', action);

			if (action.question === null) return state;

			// Only for a question this collection knows (a row, or a deletion version), and only when the
			// fetched version is accepted (OWA-68).
			if (state.has(action.questionId) || state.deletedVersion(action.question.id) !== undefined) {
				st = state.clone();
				if (!applyVersion(st, action.question)) return state;

				updateStorageNoReturn([action.question]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.questions
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.questions
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.questions
			 !partial, !stateEmpty, fetchEmpty	-> D return questions in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge questions in state newer than those in action.questions
		 */
		case Actions.QUESTION_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.questions.length === 0;
			// A deletion record from the backup is evidence only: never constructed into an entity (OWA-68)
			const fetched: Array<Question | QuestionDeletionRecord> = action.questions.map(question => isDeletionRecord(question) ? question : new Question({...question, persisted: true}));
			const fetchedIds = new Set(fetched.map(question => isDeletionRecord(question) ? question.questionId : question.id));

			// A full fetch is the whole dataset, so a persisted active row it omits is dropped (deleted rows,
			// deletion versions and unsaved local rows stay). OWA-68
			const dropOmitted = (st: ArrayMap<Question>) => {
				[...st.values()].forEach(row => {
					if (!fetchedIds.has(row.id) && row.persisted && row.status !== 'D') st.delete(row);
				});
			};

			if (logging) console.debug(`⮂ questionReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} questions with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ questionReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Build a new indexed state that keeps any deletion versions (the visible state is
					// empty, but a deletion version can still reject an older row). OWA-68
					st = carryOver(state);
					fetched.forEach(question => applyVersion(st, question));

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ questionReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Question> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Question[] = [];

				// Partials include deleted rows for every entity; applyVersion keeps or removes them and
				// records their deletion version (OWA-68). Only accepted versions go to the backup.
				fetched.forEach(question => {
					if (applyVersion(st, question)) toUpdate.push(question as Question);
					else if (logging) console.debug('⮂ questionReducer: partialMerge: rejected older version of question');
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ questionReducer: empty: questions', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Drop the persisted active rows; deleted rows, deletion versions and unsaved local rows
				// stay (OWA-68)
				st = carryOver(state);
				dropOmitted(st);

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ questionReducer: fullMerge: questions', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Start from the current rows and deletion versions, apply each fetched version under the one
				// rule, then drop persisted active rows the full fetch omitted (OWA-68). A fetched row replaces
				// a local one unless the local one is strictly newer, so a stale local (persisted or not) never
				// shadows a newer server row (OWA-7 #1), and a newer row that arrived while the fetch was in
				// flight (for example a socket update during startup restore) is kept.
				st = carryOver(state);
				fetched.forEach(question => applyVersion(st, question));
				dropOmitted(st);

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.QUESTION_FETCH_ONE_FAILURE:
			console.error('QUESTION_FETCH_ONE_FAILURE', action.questionId, action.err);

			// No update to storage required
			return state;

		case Actions.QUESTION_FETCH_FAILURE:
			console.error('QUESTION_FETCH_FAILURE', action.questionId, action.err);

			// No update to storage required
			return state;

		case Actions.QUESTION_RESTORE: {
		    let question = new Question({...action.question});
            st = state.clone();

            // One acceptance rule for every incoming version (OWA-68)
            if (!applyVersion(st, question)) return state;

            return updateStorage(st, [question]);
        }
            
		case Actions.QUESTION_SAVE: {
		    // console.debug('⮂ QUESTION_SAVE reducer', action.question?.id);
		    
			// clone the question
			let question = new Question({...action.question});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new question in the state, under the one acceptance rule (OWA-68). A rejected
			// version changes nothing and is not written to the backup.
			if (!applyVersion(st, question)) return state;

			return updateStorage(st, [question]);
		}

		case Actions.QUESTION_SAVE_MANY: {
		    if (logging) console.debug('⮂ QUESTION_SAVE_MANY reducer', action.questions.map(question => question.id).join(','));
		    
			// clone the questions
			let toProcess = action.questions.map(question => new Question(question));

			st = state.clone();
			// state.clear();

			const updates: Question[] = [];

			toProcess.forEach(question => {
				// Add or update the new question in the state, under the one acceptance rule (OWA-68)
				if (applyVersion(st, question)) updates.push(question);
				else if (logging) console.debug('⮂ QUESTION_SAVE_MANY: rejected older version of question');
			});

			return updateStorage(st, updates);
		}

		case Actions.QUESTION_REMOVE: {
			// clone the question; a remove is a deleted version (the remove saga stamps it 'D' and a new
			// addedOn)
			const question = new Question({...action.question, status: 'D'});

			// Remove from UI under the one acceptance rule: the deletion version is recorded, and the row
			// is removed, or kept as a deleted row for a type that keeps them (OWA-68)
			st = state.clone();
			if (!applyVersion(st, question)) return state;

			return updateStorage(st, [question]);
		}

		case Actions.QUESTION_RESTORE_FAILURE:
		case Actions.QUESTION_SAVE_FAILURE: {
		    console.warn('QUESTION_SAVE_FAILURE reducer', action.question?.id);
		    if (!action.question?.id) {
				console.warn('QUESTION_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const question = ensure(Question, action.question);
			if (!question) {
				console.error('Could not find question after call to save');
				return state;
			}

			st = state.clone();

			const _questionRef = state.get(question.id);

			if (_questionRef && (_questionRef.addedOn === question.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _question = new Question({..._questionRef});
				_question.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_question.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.update('question', question.addedOn, {fatalError: _question.fatalError}).then(r => {
						if (r.isErr()) console.error(`⮂ questionReducer: failed to save question with fatalError info: ${r.unwrapErr().message}`);
						else if (r.unwrap() === 0) {
							// Non-document 4xx handlers may remove the row before dispatch.
							// Preserve the historical rejected-operation queue behavior.
							idb.add('question', _question).then(saved => {
								if (saved.isErr()) console.error('Could not restore rejected question: ' + saved.unwrapErr().message);
							}).catch(err => console.error('Could not restore rejected question: ' + err.message));
						}
						else console.debug('⮂ questionReducer: saved question with fatalError info');
					}).catch(err => console.error(`⮂ questionReducer: failed to save question with fatalError info: ${err.message}`));

				st.set(_question);
			}

			return updateStorage(st);
		}

		case Actions.QUESTION_SAVE_MANY_FAILURE: {
		    console.warn('QUESTION_SAVE_MANY_FAILURE reducer', action.questions.map(question => question.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.QUESTION_RESTORE_SUCCESS:
		case Actions.QUESTION_SAVE_SUCCESS: {
			if (!action.question?.id) {
				console.warn('QUESTION_SAVE_SUCCESS reducer');
                return state;
			}

			const questionRef = state.get(action.question?.id);
			if (!questionRef) {
				console.warn('Could not find question after call to save');
                return state;
			}

			st = state.clone();

			// The echoed version goes through the one acceptance rule (OWA-68). When it is older than the
			// current row, only the current row's persisted flag changes, and the current row (never the
			// rejected echo) is what the backup receives.
			if (applyVersion(st, action.question)) return updateStorage(st, [action.question]);

			if (questionRef.persisted) return state;

			// Create a new instance to avoid mutating the entity shared with old state
			const question = new Question({...questionRef, persisted: true});
			st.set(question);

			return updateStorage(st, [question]);
		}

		case Actions.QUESTION_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ QUESTION_SAVE_MANY_SUCCESS reducer', action.questions.map(question => question.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const questions: Question[] = [];

			action.questions.forEach(e => {
				const questionRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!questionRef) console.warn('Could not find question after call to save');
				// The echoed version goes through the one acceptance rule (OWA-68)
				else if (applyVersion(st, e)) questions.push(e);
				else if (!questionRef.persisted) {
					// Older echo: only the current row's persisted flag changes, and the current row is
					// what the backup receives. Create a new instance to avoid mutating the entity shared
					// with old state.
					const question = new Question({...questionRef, persisted: true});
					st.set(question);
					questions.push(question);
				}
			});

			return updateStorage(st, questions);
		}

		case Actions.QUESTION_REMOVE_FAILURE: {
		    console.warn('QUESTION_REMOVE_FAILURE action', action.question?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.QUESTION_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.QUESTION_RESTORE_MANY: {
			if (logging) console.debug('⮂ QUESTION_RESTORE_MANY reducer', action.questions.map(question => question.id).join(','));

			const toProcess = action.questions.map(question => new Question({...question}));
			st = state.clone();

			const updates: Question[] = [];

			// One acceptance rule for every incoming version (OWA-68): a deleted version removes the row and
			// records its deletion version, or keeps a deleted row for a type that keeps them.
			toProcess.forEach(question => {
				if (applyVersion(st, question)) updates.push(question);
				else if (logging) console.debug('⮂ QUESTION_RESTORE_MANY: rejected older version of question');
			});

			return updateStorage(st, updates);
		}

		case Actions.QUESTION_REMOVE_MANY: {
			if (logging) console.debug('⮂ QUESTION_REMOVE_MANY reducer', action.questions.map(question => question.id).join(','));

			// A socket delete is a deleted version, under the one acceptance rule (OWA-68)
			const toProcess = action.questions.map(question => new Question({...question, status: 'D'}));
			st = state.clone();

			const updates: Question[] = [];

			toProcess.forEach(question => {
				if (applyVersion(st, question)) updates.push(question);
				else if (logging) console.debug('⮂ QUESTION_REMOVE_MANY: rejected older version of question');
			});

			return updateStorage(st, updates);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default questions;